import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle } from 'lucide-react';

/**
 * ErrorModal - Modal grande para mostrar errores detallados
 * @param {boolean} open - Controla si el modal está abierto
 * @param {function} onClose - Función callback para cerrar el modal
 * @param {string} title - Título del error (default: "Error")
 * @param {string|array} message - Mensaje de error o array de mensajes
 * @param {number} autoCloseDuration - Duración en ms para auto-cierre (default: 7000)
 */
export default function ErrorModal({ 
  open, 
  onClose, 
  title = "Error", 
  message = "", 
  autoCloseDuration = 7000 
}) {
  useEffect(() => {
    if (open && autoCloseDuration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDuration);
      
      return () => clearTimeout(timer);
    }
  }, [open, autoCloseDuration, onClose]);

  // Normalizar mensaje a array
  const messages = Array.isArray(message) ? message : [message];
  const hasMultipleErrors = messages.length > 1;

  if (!open) return null;

  const modalContent = (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/80 animate-in fade-in-0"
        style={{ zIndex: 99999 }}
        onClick={onClose}
      />
      
      {/* Modal centrado */}
      <div 
        className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none"
        style={{ zIndex: 99999 }}
      >
        <div 
          className="bg-[#1a1a2e] border-2 border-red-500/50 text-white max-w-lg w-full rounded-lg shadow-2xl pointer-events-auto"
          style={{ 
            backdropFilter: 'blur(10px)',
            animation: 'shake 0.5s, zoom-in-95 0.2s'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header con título */}
          <div className="flex items-center gap-3 p-6 pb-4">
            <div className="p-2 bg-red-500/20 rounded-full">
              <AlertCircle className="text-red-500 w-6 h-6" strokeWidth={2.5} />
            </div>
            <h2 className="text-xl font-bold text-red-400 flex-1" style={{ fontFamily: 'Montserrat' }}>
              {title}
            </h2>
          </div>

          {/* Contenido */}
          <div className="px-6 pb-6 space-y-3">
            {/* Mensaje(s) de error */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              {hasMultipleErrors ? (
                <ul className="space-y-2">
                  {messages.map((msg, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-white/90">
                      <span className="text-red-400 font-bold mt-0.5">•</span>
                      <span className="text-sm leading-relaxed">{msg}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-white/90 text-base leading-relaxed">
                  {messages[0]}
                </p>
              )}
            </div>

            {/* Botón de cerrar */}
            <button
              onClick={onClose}
              className="w-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 font-bold py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500/50"
            >
              Cerrar
            </button>

            {/* Barra de progreso para auto-cierre */}
            {autoCloseDuration > 0 && (
              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-red-500/60 rounded-full"
                  style={{
                    animation: `progress ${autoCloseDuration}ms linear forwards`
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        @keyframes progress {
          from { width: 100%; }
          to { width: 0%; }
        }
        
        @keyframes zoom-in-95 {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </>
  );

  // Renderizar en portal directo al body
  return createPortal(modalContent, document.body);
}
